/**
 * File: zohoReports.ts
 * Path: packages/config/src/accounting/zohoReports.ts
 * Description: Reading the reports Liam exports from Zoho Books at a month
 * end, for the Compare with Zoho check (monthEndCheck.ts).
 *
 * Zoho's Reports > (report) > Export gives a CSV (or Excel, which the admin
 * turns into the same CSV) laid out like the screen: a few title lines (the
 * organisation, the report's name, "As of 30/09/2026" or "From 01/09/2026 To
 * 30/09/2026"), a header row, group headings with no amounts ("Assets",
 * "Operating Income"), the rows, and "Total for ..." lines. So each reader
 * finds the header row by its column names, reads the period from the title
 * lines, keeps rows that carry an amount and skips headings and totals.
 * Amounts may be plain ("1234.50"), grouped ("1,234.50") or carry the rand
 * sign; a dash or a blank is nil. Dates are read as Zoho writes them for a
 * South African organisation (dd/mm/yyyy), or as yyyy-mm-dd.
 *
 * Nothing here knows the books; it only turns the files into figures.
 */

import { parseCsv } from "./csv";
import { parseRandToCents } from "./money";

const norm = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const pad = (n: number) => String(n).padStart(2, "0");

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

const validDay = (y: number, m: number, d: number): string | null => {
  if (m < 1 || m > 12 || d < 1 || y < 1900 || y > 2999) return null;
  const iso = `${y}-${pad(m)}-${pad(d)}`;
  return new Date(`${iso}T00:00:00Z`).toISOString().slice(0, 10) === iso ? iso : null;
};

const DATE_PATTERNS: { re: RegExp; read: (m: RegExpMatchArray) => string | null }[] = [
  { re: /(\d{4})-(\d{1,2})-(\d{1,2})/g, read: (m) => validDay(Number(m[1]), Number(m[2]), Number(m[3])) },
  { re: /(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})/g, read: (m) => validDay(Number(m[3]), Number(m[2]), Number(m[1])) },
  {
    re: /(\d{1,2})[\s-]+([a-z]{3,9})[\s,-]+(\d{4})/gi,
    read: (m) => validDay(Number(m[3]), MONTHS.indexOf(m[2].slice(0, 3).toLowerCase()) + 1, Number(m[1])),
  },
  {
    re: /([a-z]{3,9})\s+(\d{1,2}),?\s+(\d{4})/gi,
    read: (m) => validDay(Number(m[3]), MONTHS.indexOf(m[1].slice(0, 3).toLowerCase()) + 1, Number(m[2])),
  },
];

/** Every date written in `text`, in the order they appear. */
export const zohoDatesIn = (text: string): string[] => {
  const found: { at: number; iso: string }[] = [];
  for (const { re, read } of DATE_PATTERNS) {
    for (const m of text.matchAll(re)) {
      const iso = read(m);
      if (iso && !found.some((f) => f.at === m.index)) found.push({ at: m.index ?? 0, iso });
    }
  }
  return found.sort((a, b) => a.at - b.at).map((f) => f.iso);
};

/** The last day of the month `iso` is in. */
export const monthEndOf = (iso: string): string => {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  return `${y}-${pad(m)}-${pad(new Date(Date.UTC(y, m, 0)).getUTCDate())}`;
};

/** "2026-09" when `from`..`to` is exactly one calendar month, else null. */
export const calendarMonthOf = (from: string, to: string): string | null =>
  from.endsWith("-01") && from.slice(0, 7) === to.slice(0, 7) && monthEndOf(from) === to ? from.slice(0, 7) : null;

/** A column label such as "Sep 2026", "September 2026" or "2026-09" as "2026-09". */
const monthLabel = (label: string): string | null => {
  const text = label.trim().toLowerCase();
  const named = /^([a-z]{3,9})[\s-]+(\d{4})$/.exec(text);
  if (named) {
    const m = MONTHS.indexOf(named[1].slice(0, 3)) + 1;
    return m > 0 ? `${named[2]}-${pad(m)}` : null;
  }
  const iso = /^(\d{4})-(\d{1,2})$/.exec(text) ?? null;
  if (iso && Number(iso[2]) >= 1 && Number(iso[2]) <= 12) return `${iso[1]}-${pad(Number(iso[2]))}`;
  const slash = /^(\d{1,2})\/(\d{4})$/.exec(text);
  if (slash && Number(slash[1]) >= 1 && Number(slash[1]) <= 12) return `${slash[2]}-${pad(Number(slash[1]))}`;
  return null;
};

/** The period a report covers, read from its title lines. `from` is null for an "as of" report. */
export interface ZohoPeriod {
  from: string | null;
  to: string;
}

const readPeriod = (lines: readonly string[][]): ZohoPeriod | null => {
  for (const row of lines) {
    const text = row.filter(Boolean).join(" ");
    const dates = zohoDatesIn(text);
    if (dates.length >= 2 && /\bto\b|-|–/i.test(text)) return { from: dates[0], to: dates[1] };
    if (dates.length === 1 && /\bas (of|at|on)\b/i.test(text)) return { from: null, to: dates[0] };
  }
  return null;
};

/** A cell read as cents: blank or a dash is nil; null when it isn't an amount. */
const cellCents = (cell: string | undefined): number | null => {
  const text = (cell ?? "").trim();
  if (text === "" || text === "-" || text === "–") return 0;
  // Excel keeps sums as floating point, so a total can arrive as "44.51000000000022".
  if (/^-?\d+\.\d{3,}$/.test(text) && Math.abs(Number(text) * 100 - Math.round(Number(text) * 100)) < 0.001) return parseRandToCents(Number(text).toFixed(2));
  return parseRandToCents(text);
};

const isTotalRow = (name: string) =>
  /^(total|gross profit|operating profit|net profit|net loss|net income|profit|loss)\b/i.test(name.trim());

interface Table {
  /** The rows above the header (the title lines). */
  title: string[][];
  header: string[];
  /** The header row as written, for messages and month labels. */
  rawHeader: string[];
  /** Data rows with their line number in the file (from 1). */
  rows: { line: number; cells: string[] }[];
}

const findTable = (csv: string, isHeader: (cells: string[]) => boolean): Table | null => {
  const table = parseCsv(csv, true);
  const at = table.findIndex((row) => isHeader(row.map(norm)));
  if (at === -1) return null;
  return {
    title: table.slice(0, at),
    header: table[at].map(norm),
    rawHeader: table[at].map((c) => c.trim()),
    rows: table
      .slice(at + 1)
      .map((cells, i) => ({ line: at + i + 2, cells }))
      .filter((r) => r.cells.some((c) => c.trim() !== "")),
  };
};

const column = (header: readonly string[], ...names: string[]) => {
  for (const name of names) {
    const i = header.indexOf(name);
    if (i !== -1) return i;
  }
  return -1;
};

const NAME_COLUMNS = ["account", "account name", "name"];
const CODE_COLUMNS = ["account code", "code"];

/* ---------------------------------------------------------------------------
 * Trial Balance
 * ------------------------------------------------------------------------ */

export interface ZohoAccountRow {
  /** Where the row is in the file, for messages. */
  line: number;
  name: string;
  code: string;
}

export interface ZohoTrialBalanceRow extends ZohoAccountRow {
  /** Debit minus credit. */
  cents: number;
}

export interface ZohoTrialBalance {
  period: ZohoPeriod | null;
  rows: ZohoTrialBalanceRow[];
  totalDebitCents: number;
  totalCreditCents: number;
  /** Problems with the file itself; empty when it can be used. */
  issues: string[];
}

/** Zoho's Trial Balance export: Account, Account Code, Net Debit, Net Credit. */
export const readZohoTrialBalance = (csv: string): ZohoTrialBalance => {
  const table = findTable(
    csv,
    (cells) => cells.some((c) => NAME_COLUMNS.includes(c)) && cells.some((c) => c.includes("debit")) && cells.some((c) => c.includes("credit")),
  );
  if (!table) {
    return {
      period: null,
      rows: [],
      totalDebitCents: 0,
      totalCreditCents: 0,
      issues: ["This isn't a trial balance: no row has Account, Debit and Credit columns. In Zoho Books open Reports, Trial Balance, and export it as CSV or XLSX."],
    };
  }
  const name = column(table.header, ...NAME_COLUMNS);
  const code = column(table.header, ...CODE_COLUMNS);
  const debit = table.header.findIndex((c) => c.includes("debit"));
  const credit = table.header.findIndex((c) => c.includes("credit"));
  const issues: string[] = [];
  const rows: ZohoTrialBalanceRow[] = [];
  for (const { line, cells } of table.rows) {
    const label = (cells[name] ?? "").trim();
    if (label === "" || isTotalRow(label)) continue;
    const d = cellCents(cells[debit]);
    const c = cellCents(cells[credit]);
    if (d === null || c === null) {
      issues.push(`Line ${line} (${label}): the amount can't be read.`);
      continue;
    }
    if (d === 0 && c === 0) continue;
    rows.push({ line, name: label, code: code >= 0 ? (cells[code] ?? "").trim() : "", cents: d - c });
  }
  const totalDebitCents = rows.reduce((s, r) => s + Math.max(0, r.cents), 0);
  const totalCreditCents = rows.reduce((s, r) => s + Math.max(0, -r.cents), 0);
  if (rows.length === 0 && issues.length === 0) issues.push("The trial balance has no account balances in it.");
  if (totalDebitCents !== totalCreditCents) {
    issues.push("The debits and credits in Zoho's trial balance don't balance. Check it is the whole report, not a part of it.");
  }
  return { period: readPeriod(table.title), rows, totalDebitCents, totalCreditCents, issues };
};

/* ---------------------------------------------------------------------------
 * Profit and Loss, one column per month
 * ------------------------------------------------------------------------ */

export type ZohoPnlSide = "income" | "expense";

export interface ZohoPnlRow extends ZohoAccountRow {
  side: ZohoPnlSide;
  /** By month ("2026-09"), the way Zoho shows it: income and expenses both positive. */
  cents: Record<string, number>;
}

export interface ZohoProfitAndLoss {
  period: ZohoPeriod | null;
  /** The months the columns are, oldest first. */
  months: string[];
  rows: ZohoPnlRow[];
  /** Zoho's own Net Profit/Loss line, by month, when the file has one. */
  netProfit: Record<string, number> | null;
  issues: string[];
}

const INCOME_HEADING = /\b(income|revenue|sales)\b/i;
const EXPENSE_HEADING = /\b(expense|expenses|cost of goods sold|cost of sales)\b/i;

/**
 * Zoho's Profit and Loss export. One amount column is the month the title
 * gives (or `fallbackMonth` when it gives none); with Compare With, Previous
 * Period(s), each column is a month, named "Sep 2026" or by its dates.
 */
export const readZohoProfitAndLoss = (csv: string, fallbackMonth: string): ZohoProfitAndLoss => {
  const empty = (issue: string): ZohoProfitAndLoss => ({ period: null, months: [], rows: [], netProfit: null, issues: [issue] });
  const table = findTable(csv, (cells) => cells.some((c) => NAME_COLUMNS.includes(c)) && cells.length >= 2);
  if (!table) return empty("This isn't a profit and loss report: no row has an Account column. In Zoho Books open Reports, Profit and Loss, and export it as CSV or XLSX.");
  const period = readPeriod(table.title);
  const name = column(table.header, ...NAME_COLUMNS);
  const code = column(table.header, ...CODE_COLUMNS);
  const amountColumns = table.header
    .map((label, i) => ({ label, i }))
    .filter(({ label, i }) => i !== name && i !== code && label !== "" && label !== "account id");
  const issues: string[] = [];
  const months: { i: number; month: string }[] = [];
  for (const { label, i } of amountColumns) {
    const original = table.rawHeader[i] ?? label;
    const dates = zohoDatesIn(original);
    let month = monthLabel(original);
    if (!month && dates.length >= 2) month = calendarMonthOf(dates[0], dates[1]);
    if (!month && amountColumns.length === 1) {
      month = period?.from ? calendarMonthOf(period.from, period.to) : fallbackMonth;
      if (!month) issues.push(`The report runs from ${period?.from} to ${period?.to}. Run it for one calendar month, or with Compare With, Previous Period(s) for a column per month.`);
    }
    if (!month && amountColumns.length > 1 && /^(total|amount)$/.test(label)) continue;
    if (!month) {
      if (amountColumns.length > 1) issues.push(`The column "${original}" isn't one calendar month.`);
      continue;
    }
    if (months.some((m) => m.month === month)) {
      issues.push(`Two columns are for ${month}.`);
      continue;
    }
    months.push({ i, month });
  }
  if (months.length === 0 && issues.length === 0) issues.push("No month columns were found in the profit and loss report.");

  const rows: ZohoPnlRow[] = [];
  let side: ZohoPnlSide | null = null;
  let netProfit: Record<string, number> | null = null;
  for (const { line, cells } of table.rows) {
    const label = (cells[name] ?? "").trim();
    if (label === "") continue;
    const amounts = months.map(({ i, month }) => ({ month, cents: cellCents(cells[i]), blank: (cells[i] ?? "").trim() === "" }));
    if (amounts.every((a) => a.blank)) {
      if (EXPENSE_HEADING.test(label)) side = "expense";
      else if (INCOME_HEADING.test(label)) side = "income";
      continue;
    }
    if (/^net (profit|loss|income)/i.test(label)) {
      netProfit = Object.fromEntries(amounts.map((a) => [a.month, a.cents ?? 0]));
      continue;
    }
    if (isTotalRow(label)) continue;
    if (amounts.some((a) => a.cents === null)) {
      issues.push(`Line ${line} (${label}): an amount can't be read.`);
      continue;
    }
    if (!side) {
      issues.push(`Line ${line} (${label}): it isn't under an income or expense heading, so it was left out.`);
      continue;
    }
    rows.push({
      line,
      name: label,
      code: code >= 0 ? (cells[code] ?? "").trim() : "",
      side,
      cents: Object.fromEntries(amounts.map((a) => [a.month, a.cents ?? 0])),
    });
  }
  return { period, months: months.map((m) => m.month).sort(), rows, netProfit, issues };
};

/* ---------------------------------------------------------------------------
 * Sales by Customer
 * ------------------------------------------------------------------------ */

export interface ZohoCustomerSales {
  line: number;
  customer: string;
  /** Sales before VAT. */
  cents: number;
}

export interface ZohoSalesByCustomer {
  period: ZohoPeriod | null;
  rows: ZohoCustomerSales[];
  issues: string[];
}

/** Zoho's Sales by Customer export: Customer Name, Invoice Count, Sales, Sales With Tax. */
export const readZohoSalesByCustomer = (csv: string): ZohoSalesByCustomer => {
  const table = findTable(
    csv,
    (cells) => cells.some((c) => c === "customer name" || c === "customer" || c === "name") && cells.some((c) => c === "sales" || c === "amount" || c === "total"),
  );
  if (!table) {
    return {
      period: null,
      rows: [],
      issues: ["This isn't Sales by Customer: no row has Customer Name and Sales columns. In Zoho Books open Reports, Sales by Customer, and export it as CSV or XLSX."],
    };
  }
  const customer = column(table.header, "customer name", "customer", "name");
  const sales = column(table.header, "sales", "amount", "total");
  const issues: string[] = [];
  const rows: ZohoCustomerSales[] = [];
  for (const { line, cells } of table.rows) {
    const label = (cells[customer] ?? "").trim();
    if (label === "" || /^total\b/i.test(label)) continue;
    const cents = cellCents(cells[sales]);
    if (cents === null) {
      issues.push(`Line ${line} (${label}): the amount can't be read.`);
      continue;
    }
    rows.push({ line, customer: label, cents });
  }
  return { period: readPeriod(table.title), rows, issues };
};

/* ---------------------------------------------------------------------------
 * Receivables: AR Aging Details (or Invoice Details)
 * ------------------------------------------------------------------------ */

export interface ZohoReceivable {
  line: number;
  /** As Zoho wrote it: "INV-000036", "CN-000002" or a payment number. */
  number: string;
  customer: string;
  /** "Invoice", "Credit Note", ...; empty when the report has no Type column. */
  type: string;
  date: string | null;
  dueOn: string | null;
  /** Days past due, from Zoho's Age column, when it has one. */
  ageDays: number | null;
  balanceCents: number;
}

export interface ZohoReceivables {
  period: ZohoPeriod | null;
  rows: ZohoReceivable[];
  issues: string[];
}

const NUMBER_COLUMNS = ["transaction", "transaction number", "transaction no", "invoice", "invoice number", "invoice no", "number"];
const BALANCE_COLUMNS = ["balance due", "balance", "open balance", "amount due"];

/**
 * Zoho's AR Aging Details export (as of the month end) or its Invoice
 * Details export: a number, the customer, the balance still due, and the
 * age or the due date when the report has them.
 */
export const readZohoReceivables = (csv: string): ZohoReceivables => {
  const table = findTable(csv, (cells) => cells.some((c) => NUMBER_COLUMNS.includes(c)) && cells.some((c) => BALANCE_COLUMNS.includes(c)));
  if (!table) {
    return {
      period: null,
      rows: [],
      issues: ["This isn't a receivables report: no row has a Transaction# (or Invoice#) column and a Balance Due column. In Zoho Books open Reports, AR Aging Details, and export it as CSV or XLSX."],
    };
  }
  const number = column(table.header, ...NUMBER_COLUMNS);
  const balance = column(table.header, ...BALANCE_COLUMNS);
  const customer = column(table.header, "customer name", "customer");
  const type = column(table.header, "type", "transaction type");
  const date = column(table.header, "date", "invoice date", "transaction date");
  const due = column(table.header, "due date");
  const age = column(table.header, "age", "days overdue", "overdue days");
  const issues: string[] = [];
  const rows: ZohoReceivable[] = [];
  const cell = (cells: string[], i: number) => (i >= 0 ? (cells[i] ?? "").trim() : "");
  for (const { line, cells } of table.rows) {
    const ref = cell(cells, number);
    if (ref === "" || /^total\b/i.test(ref) || /^total\b/i.test(cell(cells, 0))) continue;
    const cents = cellCents(cells[balance]);
    if (cents === null) {
      issues.push(`Line ${line} (${ref}): the balance due can't be read.`);
      continue;
    }
    const ageText = cell(cells, age);
    const ageMatch = /-?\d+/.exec(ageText);
    rows.push({
      line,
      number: ref.toUpperCase(),
      customer: cell(cells, customer),
      type: cell(cells, type),
      date: zohoDatesIn(cell(cells, date))[0] ?? null,
      dueOn: zohoDatesIn(cell(cells, due))[0] ?? null,
      ageDays: ageMatch ? Math.max(0, Number(ageMatch[0])) : null,
      balanceCents: cents,
    });
  }
  return { period: readPeriod(table.title), rows, issues };
};
